import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_FLOW = ["new", "preparing", "ready", "completed", "cancelled"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [specials, setSpecials] = useState([]);
  const [bases, setBases] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");

  const [specialDlg, setSpecialDlg] = useState(null); // null | {id?, name, ...}
  const [baseDlg, setBaseDlg] = useState(null);
  const [flavorDlg, setFlavorDlg] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("wre_admin_token")) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("wre_admin_token");
    navigate("/admin/login");
  };

  const loadAll = useCallback(async () => {
    try {
      const [s, b, f, o] = await Promise.all([
        api.get("/admin/specials"),
        api.get("/admin/base-drinks"),
        api.get("/admin/flavors"),
        api.get("/admin/orders", { params: statusFilter ? { status: statusFilter } : {} }),
      ]);
      setSpecials(s.data); setBases(b.data); setFlavors(f.data); setOrders(o.data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired");
        logout();
      } else {
        toast.error("Failed to load data");
      }
    }
    // eslint-disable-next-line
  }, [statusFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ---- Specials handlers ----
  const saveSpecial = async () => {
    const s = specialDlg;
    if (!s.name || !s.price) return toast.error("Name and price required");
    try {
      if (s.id) {
        await api.put(`/admin/specials/${s.id}`, s);
      } else {
        await api.post(`/admin/specials`, s);
      }
      toast.success("Saved");
      setSpecialDlg(null);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deleteSpecial = async (id) => {
    if (!window.confirm("Delete this special?")) return;
    await api.delete(`/admin/specials/${id}`);
    toast.success("Deleted"); loadAll();
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.path;
  };

  // ---- Base drinks ----
  const saveBase = async () => {
    const b = baseDlg;
    if (!b.drink_type || !b.size || !b.price) return toast.error("All fields required");
    try {
      if (b.id) await api.put(`/admin/base-drinks/${b.id}`, b);
      else await api.post(`/admin/base-drinks`, b);
      toast.success("Saved"); setBaseDlg(null); loadAll();
    } catch (e) { toast.error("Save failed"); }
  };
  const deleteBase = async (id) => {
    if (!window.confirm("Delete this drink option?")) return;
    await api.delete(`/admin/base-drinks/${id}`);
    toast.success("Deleted"); loadAll();
  };

  // ---- Flavors ----
  const saveFlavor = async () => {
    const f = flavorDlg;
    if (!f.name) return toast.error("Name required");
    try {
      if (f.id) await api.put(`/admin/flavors/${f.id}`, f);
      else await api.post(`/admin/flavors`, f);
      toast.success("Saved"); setFlavorDlg(null); loadAll();
    } catch (e) { toast.error("Save failed"); }
  };
  const deleteFlavor = async (id) => {
    if (!window.confirm("Delete this flavor?")) return;
    await api.delete(`/admin/flavors/${id}`);
    toast.success("Deleted"); loadAll();
  };

  // ---- Orders ----
  const updateOrderStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}/status`, { status });
    toast.success("Status updated"); loadAll();
  };

  const statusColors = {
    new: "bg-accent/15 text-accent border-accent/30",
    preparing: "bg-primary/15 text-primary border-primary/30",
    ready: "bg-secondary text-secondary-foreground border-secondary",
    completed: "bg-muted text-muted-foreground border-border",
    cancelled: "bg-destructive/10 text-destructive border-destructive/30",
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/60 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground">White River Energy</p>
            <h1 className="font-display text-2xl tracking-tight">Admin Dashboard</h1>
          </div>
          <Button onClick={logout} variant="ghost" data-testid="admin-logout-btn" className="gap-2 rounded-full">
            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <Tabs defaultValue="orders">
          <TabsList className="bg-secondary/50 rounded-full p-1">
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full px-5">Orders</TabsTrigger>
            <TabsTrigger value="specials" data-testid="tab-specials" className="rounded-full px-5">Specials</TabsTrigger>
            <TabsTrigger value="drinks" data-testid="tab-drinks" className="rounded-full px-5">Drinks & Sizes</TabsTrigger>
            <TabsTrigger value="flavors" data-testid="tab-flavors" className="rounded-full px-5">Flavors</TabsTrigger>
          </TabsList>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl">All Orders ({orders.length})</h2>
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-status" className="w-48 rounded-xl"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_FLOW.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              {orders.length === 0 && <p className="text-muted-foreground">No orders yet.</p>}
              {orders.map((o) => (
                <div key={o.id} data-testid={`order-${o.id}`} className="bg-white border border-border/60 rounded-2xl p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-display text-lg">{o.order_number}</span>
                        <Badge className={`${statusColors[o.status]} rounded-full border`}>{o.status}</Badge>
                        <Badge variant="outline" className="rounded-full capitalize">{o.order_type}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {o.customer_name} · {o.customer_phone} · {new Date(o.created_at).toLocaleString()}
                      </p>
                      {o.address && <p className="text-sm text-muted-foreground">{o.address}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-display text-xl text-primary">${o.total.toFixed(2)}</span>
                      <Select value={o.status} onValueChange={(v) => updateOrderStatus(o.id, v)}>
                        <SelectTrigger data-testid={`order-status-${o.id}`} className="w-36 rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS_FLOW.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <ul className="text-sm space-y-1">
                    {o.items.map((it, idx) => (
                      <li key={idx}>
                        <span className="font-medium">{it.quantity}× {it.name}</span>
                        {it.flavors?.length > 0 && <span className="text-muted-foreground"> — {it.flavors.join(", ")}</span>}
                      </li>
                    ))}
                  </ul>
                  {o.notes && <p className="text-sm text-muted-foreground mt-2 italic">Note: {o.notes}</p>}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* SPECIALS */}
          <TabsContent value="specials" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl">Specials ({specials.length})</h2>
              <Button onClick={() => setSpecialDlg({ name: "", description: "", price: 0, drink_type: "", available: true, sort_order: 0, image_path: null })} data-testid="new-special-btn" className="rounded-full bg-primary text-primary-foreground gap-2">
                <Plus className="h-4 w-4" /> Add Special
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {specials.map((s) => (
                <div key={s.id} data-testid={`special-row-${s.id}`} className="bg-white border border-border/60 rounded-2xl overflow-hidden">
                  <div className="aspect-[16/9] bg-secondary/50">
                    {s.image_path ? <img src={fileUrl(s.image_path)} alt={s.name} className="w-full h-full object-cover" /> :
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">No image</div>}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-display">{s.name}</p>
                        <p className="text-sm text-muted-foreground">{s.drink_type} · ${s.price.toFixed(2)}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setSpecialDlg(s)} data-testid={`edit-special-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteSpecial(s.id)} data-testid={`delete-special-${s.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{s.description}</p>
                    {!s.available && <Badge variant="outline" className="mt-2">unavailable</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* BASE DRINKS */}
          <TabsContent value="drinks" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl">Drink Types & Sizes ({bases.length})</h2>
              <Button onClick={() => setBaseDlg({ drink_type: "", size: "", price: 0, available: true })} data-testid="new-base-btn" className="rounded-full bg-primary text-primary-foreground gap-2">
                <Plus className="h-4 w-4" /> Add Drink/Size
              </Button>
            </div>
            <div className="bg-white border border-border/60 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr><th className="text-left p-4">Drink Type</th><th className="text-left p-4">Size</th><th className="text-left p-4">Price</th><th className="text-left p-4">Available</th><th className="p-4"></th></tr>
                </thead>
                <tbody>
                  {bases.map((b) => (
                    <tr key={b.id} data-testid={`base-row-${b.id}`} className="border-t border-border/60">
                      <td className="p-4 font-medium">{b.drink_type}</td>
                      <td className="p-4">{b.size}</td>
                      <td className="p-4">${b.price.toFixed(2)}</td>
                      <td className="p-4">{b.available ? "Yes" : "No"}</td>
                      <td className="p-4 text-right">
                        <Button size="icon" variant="ghost" onClick={() => setBaseDlg(b)} data-testid={`edit-base-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteBase(b.id)} data-testid={`delete-base-${b.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* FLAVORS */}
          <TabsContent value="flavors" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl">Flavors ({flavors.length})</h2>
              <Button onClick={() => setFlavorDlg({ name: "", available: true })} data-testid="new-flavor-btn" className="rounded-full bg-primary text-primary-foreground gap-2">
                <Plus className="h-4 w-4" /> Add Flavor
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {flavors.map((f) => (
                <div key={f.id} data-testid={`flavor-row-${f.id}`} className="bg-white border border-border/60 rounded-xl px-4 py-3 flex items-center justify-between">
                  <span className={f.available ? "" : "text-muted-foreground line-through"}>{f.name}</span>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setFlavorDlg(f)} data-testid={`edit-flavor-${f.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => deleteFlavor(f.id)} data-testid={`delete-flavor-${f.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Special dialog */}
      <Dialog open={!!specialDlg} onOpenChange={(o) => !o && setSpecialDlg(null)}>
        <DialogContent className="bg-white rounded-3xl">
          <DialogHeader><DialogTitle>{specialDlg?.id ? "Edit" : "New"} Special</DialogTitle></DialogHeader>
          {specialDlg && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="special-name-input" value={specialDlg.name} onChange={(e) => setSpecialDlg({...specialDlg, name: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea data-testid="special-desc-input" value={specialDlg.description || ""} onChange={(e) => setSpecialDlg({...specialDlg, description: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Price ($)</Label><Input type="number" step="0.01" data-testid="special-price-input" value={specialDlg.price} onChange={(e) => setSpecialDlg({...specialDlg, price: parseFloat(e.target.value) || 0})} /></div>
                <div><Label>Drink Type (tag)</Label><Input data-testid="special-type-input" value={specialDlg.drink_type || ""} onChange={(e) => setSpecialDlg({...specialDlg, drink_type: e.target.value})} placeholder="Red Bull / Lotus" /></div>
              </div>
              <div><Label>Image</Label>
                {specialDlg.image_path && <img src={fileUrl(specialDlg.image_path)} alt="" className="h-24 w-24 rounded-xl object-cover mb-2" />}
                <Input type="file" accept="image/*" data-testid="special-image-input" onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  try {
                    const path = await uploadImage(file);
                    setSpecialDlg({...specialDlg, image_path: path});
                    toast.success("Image uploaded");
                  } catch { toast.error("Upload failed"); }
                }} />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={specialDlg.available} onCheckedChange={(v) => setSpecialDlg({...specialDlg, available: v})} data-testid="special-available-switch" />
                <Label>Available</Label>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveSpecial} data-testid="save-special-btn" className="rounded-full bg-primary text-primary-foreground">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Base drink dialog */}
      <Dialog open={!!baseDlg} onOpenChange={(o) => !o && setBaseDlg(null)}>
        <DialogContent className="bg-white rounded-3xl">
          <DialogHeader><DialogTitle>{baseDlg?.id ? "Edit" : "New"} Drink/Size</DialogTitle></DialogHeader>
          {baseDlg && (
            <div className="space-y-3">
              <div><Label>Drink Type</Label><Input data-testid="base-type-input" value={baseDlg.drink_type} onChange={(e) => setBaseDlg({...baseDlg, drink_type: e.target.value})} placeholder="Red Bull" /></div>
              <div><Label>Size</Label><Input data-testid="base-size-input" value={baseDlg.size} onChange={(e) => setBaseDlg({...baseDlg, size: e.target.value})} placeholder="24oz" /></div>
              <div><Label>Price ($)</Label><Input type="number" step="0.01" data-testid="base-price-input" value={baseDlg.price} onChange={(e) => setBaseDlg({...baseDlg, price: parseFloat(e.target.value) || 0})} /></div>
              <div className="flex items-center gap-3">
                <Switch checked={baseDlg.available} onCheckedChange={(v) => setBaseDlg({...baseDlg, available: v})} data-testid="base-available-switch" />
                <Label>Available</Label>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveBase} data-testid="save-base-btn" className="rounded-full bg-primary text-primary-foreground">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flavor dialog */}
      <Dialog open={!!flavorDlg} onOpenChange={(o) => !o && setFlavorDlg(null)}>
        <DialogContent className="bg-white rounded-3xl">
          <DialogHeader><DialogTitle>{flavorDlg?.id ? "Edit" : "New"} Flavor</DialogTitle></DialogHeader>
          {flavorDlg && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="flavor-name-input" value={flavorDlg.name} onChange={(e) => setFlavorDlg({...flavorDlg, name: e.target.value})} /></div>
              <div className="flex items-center gap-3">
                <Switch checked={flavorDlg.available} onCheckedChange={(v) => setFlavorDlg({...flavorDlg, available: v})} data-testid="flavor-available-switch" />
                <Label>Available</Label>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveFlavor} data-testid="save-flavor-btn" className="rounded-full bg-primary text-primary-foreground">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
